import React, { MouseEvent, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import CloseIcon from '@videoblocks/react-icons/Close';

import { SearchFilterContentTypes } from '../../../../Shared/enums';

interface RenderInputComponentProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  defaultValue?: string;
  useNewSearchUI?: boolean;
  showClearButton?: boolean;
  onClearSearchTerm?: (event: MouseEvent<HTMLButtonElement>) => void;
  showSuggestions?: boolean;
  contentType?: SearchFilterContentTypes;
}

const AutoSuggestInput = React.forwardRef<
  HTMLInputElement,
  RenderInputComponentProps
>(
  (
    {
      defaultValue,
      className,
      placeholder = 'Search...',
      useNewSearchUI,
      showClearButton,
      onClearSearchTerm,
      onFocus,
      onBlur,
      showSuggestions,
      contentType,
      ...rest
    }: RenderInputComponentProps,
    ref
  ) => {
    const [dynamicPlaceholder, setDynamicPlaceholder] = useState(placeholder);

    useEffect(() => {
      // placeholder prop can change with AI CTA
      setDynamicPlaceholder(placeholder);
    }, [placeholder]);

    const ariaLabelInput = [
      'Input Search',
      contentType?.replace(/[_-]/g, ' ').toUpperCase(),
    ]
      .filter(Boolean)
      .join(' ');

    if (useNewSearchUI) {
      return (
        <div className="flex flex-row items-center">
          <input
            id="search-input"
            type="text"
            autoComplete="off"
            ref={ref}
            aria-label={ariaLabelInput}
            data-cy="unifiedNav-searchInput"
            data-public={true}
            placeholder={dynamicPlaceholder}
            className={twMerge(
              'unifiedNav-searchInput rounded-l-xl-35 border-none shadow-none font-semibold h-full outline-none w-full text-lg px-3',
              className
            )}
            defaultValue={defaultValue}
            onFocus={(e) => {
              setDynamicPlaceholder('');
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setDynamicPlaceholder(placeholder);
              onBlur?.(e);
            }}
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls="collectionsSuggestion-autoSuggestionList"
            aria-owns="collectionsSuggestion-autoSuggestionList"
            aria-haspopup="listbox"
            aria-activedescendant=""
            {...rest}
          />
          <div className="p-4">
            {showClearButton && (
              <button
                id="clear-search-term-button"
                type="button"
                onClick={onClearSearchTerm}
                className={'w-4 h-4 border-none'}
                aria-label="Clear input search"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>
      );
    }
    return (
      <input
        autoComplete="off"
        ref={ref}
        aria-label={ariaLabelInput}
        data-cy="unifiedNav-searchInput"
        data-public={true}
        placeholder={placeholder}
        className={twMerge(
          'unifiedNav-searchInput rounded-l-xl-35 border-none shadow-none font-semibold h-full outline-none w-full py-2 text-sm px-3',
          className
        )}
        defaultValue={defaultValue}
        onFocus={onFocus}
        onBlur={onBlur}
        role="combobox"
        aria-expanded={showSuggestions}
        aria-controls="navSearchBox-autoSuggestionList"
        aria-owns="navSearchBox-autoSuggestionList"
        aria-haspopup="listbox"
        aria-activedescendant=""
        {...rest}
      />
    );
  }
);

AutoSuggestInput.displayName = 'AutoSuggestInput';

export default AutoSuggestInput;
